import React, {useEffect, useRef} from "react";
import {getAttributes, SUPPLIER_SYNDICATION_ENABLED, useDidUpdateEffect, utilGetPartners, utilSearchLastImport, utilSearchLastImports} from "../../../../../utils/utils";
import axios from "axios";
import Config from "../../../../../config";
import {loadProfiles} from "../../../../files/import_export_profiles";
import {Helpers, StorageHelper} from "../../../../../helpers";
import cloneDeep from 'lodash/cloneDeep';
import {Grid, IconButton, Paper, TableCell, Typography} from "@mui/material";
import ProductImport from "./components/ProductImport";
import SelectCategory from "./components/SelectCategory";
import CategoryMapping from "./components/CategoryMapping";
import AttributeMapping from "./components/AttributeMapping";
import ListMapping from "./components/ListMapping";
import Review, {formatProfileAttributeRulesToRules, formatProfileCategoryMappingsToRules, formatProfileListMappingsToRules, formatProfileToColumns} from "./components/Review";
import EnhancedContentLoader from "../../../../../components/EnhancedContentLoader";
import {ArrowBack} from "@mui/icons-material";
import EnhancedStepper from "../../../../../components/EnhancedStepper";
import EnhancedErrorDialog from "../../../../../components/EnhancedErrorDialog";
import { styled } from '@mui/material/styles';
import {pageContentGap, Header} from "../../../../../theme";
import {SaveProfile} from "../../export/components/EnhancedProfile";
import UpdatingDialog from "../../../../../components/Dialogs/UpdatingDialog";
import {formatProfileFromRemote, unescapeField, unescapeFieldsInCondition, detectFileType, isListTypeAttribute} from "./profile_helpers";
import type {RemoteProfile} from "./profile_helpers";
import type {ImportProfile} from "../../../types";
import type {WizardStep, RetailerAttribute, PartnerInfo, ParsedField, ParsedFieldWithData} from "./types";

// Re-export profile helper utilities for backwards compatibility with existing
// out-of-scope consumers (e.g. channel_settings.js).
export {formatProfileFromRemote, unescapeField, unescapeFieldsInCondition};

// region TypeScript Interfaces
interface ErrorState {
    open: boolean;
    errorHeader: string;
    errorMessage: unknown;
}

interface CatalogManagerProductAdvancedImportProps {
    isRetailer?: boolean;
    retailers?: Array<Record<string, unknown>>;
    setTitle?: (title: string, hideBackButton?: boolean) => void;
    history: any;
    location: any;
}
// endregion

const iconButtonSx = (theme: any) => ({
    backgroundColor: "white",
    border: "none",
    borderRadius: "6px",
    "& svg": { color: "rgba(0, 0, 0, 0.54)" }
});
const MappingHeader = styled(Typography)(({ theme }) => ({
    color: "black",
    fontWeight: 700
}));
const MappingCell = styled(TableCell)(({ theme }) => ({
    margin: "12px",
    border: "none"
}));
export { MappingHeader, MappingCell };
const storageHelper = new StorageHelper();
const blankProfile: ImportProfile = {
    DocumentType: "ProductOnboardingCenterCatalog",
    Name: undefined,
    Partner: "",
    Category: "",
    CategoryMappings: {},
    AttributeMappings: {},
    ListMappings: {},
    AttributeMappingRules: []
};
const unimportableAttributesAsSupplier = [
    "approval_date", "approval_status", "approved_by", "private_note", "public_note", "sync_status", "listed_by", "listed_date", "delisted_by", "delisted_date", "created_date"
];
const unimportableAttributesAsRetailer = [
    "private_note", "public_note", "created_date"
];
function getStepContent(step: number, props: any) {
    switch (step) {
        case 0:
            return <ProductImport {...props}/>;
        case 1:
            return <SelectCategory {...props}/>;
        case 2:
            return <CategoryMapping {...props}/>;
        case 3:
            return <AttributeMapping {...props} fileParsedFields={props.parsedFields} />;
        case 4:
            return <ListMapping {...props}/>;
        case 5:
            return <Review {...props}/>;
        default:
            return <></>
    }
}
export default function CatalogManagerProductAdvancedImport(props: CatalogManagerProductAdvancedImportProps) {
    const abortController = useRef<AbortController | null>(null);
    const isMarketplace = props.retailers && props.retailers.some((retailer: any) => retailer.Marketplace === true);
    const urlStartingStep = new (Helpers as any)().queryString(props.location.search)["startingStep"];
    const urlPartnerId = new (Helpers as any)().queryString(props.location.search)["partnerId"];
    const urlProfileName = new (Helpers as any)().queryString(props.location.search)["profile"];
    const storageProfileName = "LBCatalogSupplierImportProfile";
    let profileType = "ImportCatalogItems";
    const [steps, setSteps] = React.useState<WizardStep[]>(
        [
            {label: props.isRetailer
                    ? "Product Import"
                    : isMarketplace && SUPPLIER_SYNDICATION_ENABLED
                        ? "Select Source"
                        : "Product Import",
                status: ""
            },
            {label: "Select Category", status: ""},
            {label: "Category Mapping", status: ""},
            {label: "Attribute Mapping", status: ""},
            {label: "List Mapping", status: ""},
            {label: "Review & Submit", status: ""}
        ]
    );
    const isInitialized = useRef<boolean>(false);
    const [resfreshView, setRefreshView] = React.useState<boolean>(false);
    const [updated, setUpdated] = React.useState<boolean>(false);
    const [updating, setUpdating] = React.useState<boolean>(false);
    const [loading, setLoading] = React.useState<boolean>(false);
    // The original .js file initialized error to `false` but later setters use
    // setError({open, errorHeader, errorMessage}). The state is therefore a
    // disjoint union; keep the literal-false initial value to preserve the
    // original semantics (open?: false branch).
    const [error, setError] = React.useState<false | ErrorState>(false);
    const [activeStep, setActiveStep] = React.useState<number>(0);
    const [attributes, setAttributes] = React.useState<RetailerAttribute[]>([]);
    const [partners, setPartners] = React.useState<PartnerInfo[]>([]);
    /* We need to keep track of two profiles, one is the formatted variant to fit into
       set objects in the backend, and the other is the raw variant to fit into the UI.
       Normally you could format on save but the profile components reuse existing save logic. */
    const [profile, setProfile] = React.useState<ImportProfile>({...blankProfile});
    const [formattedProfile, setFormattedProfile] = React.useState<RemoteProfile>({} as RemoteProfile);
    const [availableProfiles, setAvailableProfiles] = React.useState<ImportProfile[]>([]);
    const previousPartner = useRef(profile.Partner);
    //region Import State
    const [hasPreviousImport, setHasPreviousImport] = React.useState<boolean>(false);
    const [previousImports, setPreviousImports] = React.useState<Array<{Partner?: unknown; Records: Array<Record<string, unknown>>}>>([]);
    const [loadingPreviousImport, setLoadingPreviousImport] = React.useState<boolean>(false); // Single file loading for PARTNER
    const [loadingPreviousFiles, setLoadingPreviousFiles] = React.useState<boolean>(false); // General file loading for ALL PARTNERS
    const [file, setFile] = React.useState<File | null>(null);
    const [parsedFields, setParsedFields] = React.useState<ParsedField[]>([]);
    const [parsedFieldsWithData, setParsedFieldsWithData] = React.useState<ParsedFieldWithData[]>([]);
    // Mirror for sync reads in fetchColumnData without making it a fetch-effect dep.
    const parsedFieldsWithDataRef = useRef<ParsedFieldWithData[]>([]);
    parsedFieldsWithDataRef.current = parsedFieldsWithData;
    // axios 0.19 uses CancelToken, not AbortController.
    const columnDataCancel = useRef<any>(null);
    const parseFileCancel = useRef<any>(null);
    // Separate from the shared `loading` so each step shows its own fetch spinner.
    const [columnDataLoading, setColumnDataLoading] = React.useState<boolean>(false);
    const [sampleData, setSampleData] = React.useState<Array<Record<string, unknown>>>([]);
    //endregion
    //region Functions
    const allowNext = () => {
        switch (activeStep) {
            case 0:
                return file !== null
                    && (profile.Partner !== "" && profile.Partner !== "000000")
                    && (profile.Name !== "" || profile.Name !== undefined);
            case 1:
                return profile.Category !== "";
            case 2:
                return true;
            case 3:
                return true;
            case 4:
                return true;
            case 5:
                return false;
            default:
                return false;
        }
    }
    const allowBack = () => {
        switch (activeStep) {
            case 0:
                return false;
            case 1:
                return true;
            case 2:
                return true;
            case 3:
                return true;
            case 4:
                return true;
            case 5:
                return true;
            default:
                return false;
        }
    }
    const nextStep = () => {
        if (!allowNext()) return;
        setActiveStep(activeStep + 1);
        setSteps((prevState) => {
            let newState = cloneDeep(prevState);
            // newState[activeStep].status = "success";
            newState[activeStep + 1].status = "active";
            return newState;
        });
    }
    const backStep = () => {
        if (!allowBack()) return;
        setActiveStep(activeStep - 1);
    }
    const onUploadDrop = async (files: File[]) => {
        if (!files || !files.length) return;
        // Cancel a prior file's in-flight fetch so its late response can't merge here.
        if (columnDataCancel.current) {
            columnDataCancel.current.cancel('file changed');
            columnDataCancel.current = null;
            setColumnDataLoading(false);
        }
        // Reset synchronously so the fetch effect waits for the new seed, not races it.
        setParsedFieldsWithData([]);
        setFile(files[0]);
        await parseFile(files[0]);
    }
    const parseFlatFileErrorMessage = (e: any): string => {
        let errors = ["Unexpected error occurred."];
        if (e.response) {
            errors = new (Helpers as any)().getApiErrors(e.response.data);
        }
        return errors.join(" ");
    };
    const parseFile = async (file: File) => {
        const fileType = detectFileType(file);
        // Cancel a prior parse so a slower earlier drop can't overwrite a newer one.
        if (parseFileCancel.current) parseFileCancel.current.cancel('superseded by a newer file');
        const source = axios.CancelToken.source();
        parseFileCancel.current = source;
        setLoadingPreviousImport(true);
        try {
            let formData = new FormData();
            formData.append("file", file);
            let parsed: any = (await axios.post(Config.api + `/odata/company/Functions.ParseFlatFile?docType=ProductOnboardingCenterCatalog&fileType=${fileType}`, formData, {
                headers: {'Content-Type': 'multipart/form-data'},
                cancelToken: source.token
            })).data;
            if (parseFileCancel.current !== source) return;
            let mappedFields: ParsedField[] = parsed.Columns.map((r: any) => ({field: r.Input, content: r.SampleContent}));
            setParsedFields(mappedFields);
            // values/duplicates are fetched lazily per step; seed them empty.
            setParsedFieldsWithData(parsed.Columns.map((r: any) => ({
                field: r.Input,
                content: r.SampleContent,
                values: undefined,
                duplicates: undefined
            })));
            let sampleData = parsed.SampleRows.filter((c: any) => {
                let values = Object.values(c);
                const result = values.every(v => v === "");
                return  !result;
            });
            setSampleData(sampleData);
        } catch (e: any) {
            if (axios.isCancel(e) || parseFileCancel.current !== source) return;
            setError({
                open: true,
                errorHeader: "Error parsing file",
                errorMessage: parseFlatFileErrorMessage(e)
            })
        } finally {
            // Only the current parse owns the loading flag (superseded ones must not clear it).
            if (parseFileCancel.current === source) {
                setLoadingPreviousImport(false);
                parseFileCancel.current = null;
            }
        }
    }
    const loadImportProfiles = async (): Promise<ImportProfile[]> => {
        let profiles: any = await (loadProfiles as any)(profileType);
        profiles = profiles.map((r: any) => formatProfileFromRemote(r));
        setAvailableProfiles(profiles);
        return profiles;
    }
    const loadPartners = async (): Promise<PartnerInfo[]> => {
        let partners: any = await (utilGetPartners as any)();
        if (partners.errorMessage) {
            setError({open: true, errorHeader: "Error", errorMessage: partners.errorMessage});
            return [];
        }
        let sortedPartners = partners.sort((a: PartnerInfo, b: PartnerInfo) => a.CompanyName.localeCompare(b.CompanyName));
        return sortedPartners;
    }
    const loadAttributes = async (partnerId: any) => {
        let attributes: any = await (getAttributes as any)(partnerId, props.isRetailer);
        if (attributes.errorMessage) {
            setError({open: true, errorHeader: "Error", errorMessage: attributes.errorMessage});
            return;
        }
        // Filter out any unimportable attributes
        let unimportableAttributes = props.isRetailer ? unimportableAttributesAsRetailer : unimportableAttributesAsSupplier;
        attributes = attributes.filter((r: any) => !unimportableAttributes.includes(r.Name));
        setAttributes(attributes);
    }
    const loadPreviousImport = async () => {
        // Cancel any existing requests
        if (abortController.current) {
            abortController.current.abort();
        }
        // Create new abort controller
        abortController.current = new AbortController();
        setLoadingPreviousImport(true);
        // Set up timeout to abort after 15 seconds
        const timeoutId = setTimeout(() => {
            if (abortController.current) {
                abortController.current.abort();
            }
        }, 15000);
        try {
            let response: any = await (utilSearchLastImport as any)(profile.Partner, abortController.current.signal);
            clearTimeout(timeoutId);
            if(!response.success) {
                setActiveStep(0);
                setError({open: true, errorHeader: "Error", errorMessage: response.errorMessage ?? "Error loading previous import file, please manually add a file or select a data source."});
            }
            else {
                let records = response.data
                if(records.length > 0) {
                    try {
                        let fileUrl = records[0].BlobPath;
                        if(fileUrl.includes("/catalogmanagerfiles/")) {
                            setHasPreviousImport(true);
                            let hashedFileName = fileUrl.split("/").pop();
                            let fileDescription = records[0].Description;
                            let formattedUrl = Config.api + `/api/v1/attachments/catalogmanagerfiles/${hashedFileName}`;
                            let downloadResponse = await axios.get(formattedUrl, ({
                                responseType: 'blob',
                                signal: abortController.current.signal
                            } as any));
                            let file = new File([downloadResponse.data], fileDescription);
                            await onUploadDrop([file]);
                        }
                    }
                    catch(e: any) {
                        if (e.name !== 'AbortError') {
                            setActiveStep(0);
                            setError({open: true, errorHeader: "Error", errorMessage: "Error loading previous import file, please manually add a file or select a data source."});
                        }
                    }
                }
            }
        } catch (e: any) {
            clearTimeout(timeoutId);
            if (e.name !== 'AbortError') {
                setActiveStep(0);
                setError({open: true, errorHeader: "Error", errorMessage: "Error loading previous import file, please manually add a file or select a data source."});
            }
        }
        setLoadingPreviousImport(false);
    }
    const loadPreviousImports = async () => {
        let previousImports: Array<{Partner?: unknown; Records: Array<Record<string, unknown>>}> = [];
        setLoadingPreviousFiles(true);
        // Create new abort controller for this request
        const abortController = new AbortController();
        // Set up timeout to abort after 15 seconds
        const timeoutId = setTimeout(() => {
            abortController.abort();
        }, 15000);
        try {
            let response: any = await (utilSearchLastImports as any)(10, abortController.signal);
            clearTimeout(timeoutId);
            if (!response.success) {
                setError({open: true, errorHeader: "Error", errorMessage: "Error pulling previous imports, please manually upload a file."});
            } else {
                response.data.forEach((result: any) => {
                    if(result.BlobPath.includes("/catalogmanagerfiles/")) {
                        previousImports.push({
                            Partner: result.PartnerCoId,
                            Records: [result]
                        });
                    }
                });
            }
        } catch (e: any) {
            clearTimeout(timeoutId);
            setError({open: true, errorHeader: "Error", errorMessage: e.message || "Error pulling previous imports, please manually upload a file."});
        }
        setLoadingPreviousFiles(false);
        return previousImports;
    }
    //endregion
    //region Lifecycle
    // Effect for initial load and partner changes
    (useDidUpdateEffect as any)(() => {
        const handlePartnerChange = async () => {
            setLoading(true);
            // Only load previous import if partner has changed, isMarketplace, and has at least one available profile
            const partnerChanged = previousPartner.current !== profile.Partner;
            const isMarketplace = props.retailers && props.retailers.some((retailer: any) => retailer.Marketplace === true);
            const availableProfilesForPartner = availableProfiles.filter((avalProfile: any) => avalProfile.Partner === profile.Partner)
            if (
                partnerChanged && profile.Partner && profile.Partner !== ""
                && isMarketplace && profile.Partner !== "000000"
                && (availableProfilesForPartner.length > 0)
            ) {
                await loadPreviousImport();
            }
            if((partnerChanged && profile.Partner && profile.Partner !== "" && profile.Partner !== "000000") || props.isRetailer) {
                await loadAttributes(profile.Partner);
            }
            if(!isInitialized.current) {
                setRefreshView(!resfreshView);
            }
            isInitialized.current = true;
            previousPartner.current = profile.Partner;
            setLoading(false);
        };
        handlePartnerChange();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [profile.Partner]);
    useEffect(() => {
        const init = async () => {
            setLoading(true);
            if(urlStartingStep) {
                setActiveStep(parseInt(urlStartingStep));
            }
            const [partnersResult, importProfilesResult] = await Promise.all([
                loadPartners(),
                loadImportProfiles(),
            ]);
            let foundProfile: any = urlPartnerId && importProfilesResult.find((r: any) => r.Partner === parseInt(urlPartnerId));
            foundProfile = (urlPartnerId && urlProfileName) ? importProfilesResult.find((r: any) => r.Name === urlProfileName && r.Partner === parseInt(urlPartnerId)) : foundProfile;
            if(!foundProfile && urlPartnerId) {
                setProfile({...blankProfile, Partner: parseInt(urlPartnerId)});
            }
            else {
                if(importProfilesResult.length > 0 && urlPartnerId) setProfile(foundProfile ? foundProfile : importProfilesResult[0]);
            }
            setPartners(partnersResult ?? []);
            setLoading(false);
        }
        const loadPreviousFiles = async () => {
            let previousImports = await loadPreviousImports();
            setPreviousImports(previousImports);
        }
        loadPreviousFiles();
        init();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);
    // Separate effect for profile formatting and steps
    useEffect(() => {
        const handleProfileFormatting = () => {
            if (parsedFields.length === 0) {
                const existingProfile: any = availableProfiles.find((r: any) => r.Name === profile.Name);
                setFormattedProfile({
                    Name: profile.Name,
                    Partner: profile.Partner,
                    Description: profile.Description,
                    DocumentType: profile.DocumentType,
                    Id: profile.Id,
                    Shared: profile.Shared,
                    Fields: existingProfile?.Fields || [],
                    Rules: existingProfile?.Rules || [],
                    Filter: ""
                } as RemoteProfile);
                return;
            }
            const formattedColumns = formatProfileToColumns(profile, parsedFields);
            const formattedListMappings = formatProfileListMappingsToRules(profile, parsedFields);
            const formattedCategoryMappings = formatProfileCategoryMappingsToRules(profile, parsedFields);
            const formattedAttributeRules = formatProfileAttributeRulesToRules(profile, parsedFields);
            setFormattedProfile({
                Name: profile.Name,
                Partner: profile.Partner,
                Description: profile.Description,
                DocumentType: profile.DocumentType,
                Fields: formattedColumns,
                Id: profile.Id,
                Shared: profile.Shared,
                Rules: [
                    ...formattedListMappings,
                    ...formattedCategoryMappings,
                    ...formattedAttributeRules
                ],
                Filter: ""
            } as RemoteProfile);
        };
        const updateSteps = () => {
            setSteps((prevState) => {
                let newState = cloneDeep(prevState);
                newState[0].status = file && profile ? "success" : "";
                newState[1].status = profile.Category ? "success" : "";
                newState[2].status = Object.keys(profile.CategoryMappings).length > 0 ? "success" : "";
                newState[3].status = (Object.keys(profile.AttributeMappings).filter(key => key !== "category").length > 0) ? "success" : "";
                newState[4].status = Object.keys(profile.ListMappings).length > 0 ? "success" : "";
                return newState;
            });
        };
        handleProfileFormatting();
        updateSteps();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [profile, parsedFields, file]);
    // Fetches only the columns the upcoming step needs (minimal request -> avoids 414);
    // skips already-populated columns and cancels a superseded fetch.
    const fetchColumnData = async (uniqueCols: string[], duplicateCols: string[]) => {
        if (!file) return;
        const current = parsedFieldsWithDataRef.current;
        const uniqueSet = Array.from(new Set(uniqueCols.filter(Boolean)))
            .filter((c) => { const e = current.find((f) => f.field === c); return !e || e.values === undefined; });
        const duplicateSet = Array.from(new Set(duplicateCols.filter(Boolean)))
            .filter((c) => { const e = current.find((f) => f.field === c); return !e || e.duplicates === undefined; });
        if (uniqueSet.length === 0 && duplicateSet.length === 0) return;
        if (columnDataCancel.current) columnDataCancel.current.cancel('superseded by a newer fetch');
        const source = axios.CancelToken.source();
        columnDataCancel.current = source;
        const fileType = detectFileType(file);
        try {
            setColumnDataLoading(true);
            const formData = new FormData();
            formData.append("file", file);
            let url = Config.api + `/odata/company/Functions.ParseFlatFile?docType=ProductOnboardingCenterCatalog&fileType=${fileType}`;
            if (uniqueSet.length) url += `&uniqueColumns=${uniqueSet.map(encodeURIComponent).join(",")}`;
            if (duplicateSet.length) url += `&duplicateColumns=${duplicateSet.map(encodeURIComponent).join(",")}`;
            const parsed: any = (await axios.post(url, formData, {
                headers: {'Content-Type': 'multipart/form-data'},
                cancelToken: source.token
            })).data;
            const byField: Record<string, any> = {};
            (parsed.Columns || []).forEach((c: any) => { byField[c.Input] = c; });
            if (columnDataCancel.current !== source) return;
            setParsedFieldsWithData((prev) => prev.map((f) => {
                const col = byField[f.field];
                if (!col) return f;
                return {
                    ...f,
                    values: uniqueSet.includes(f.field) ? (col.UniqueValues ?? []) : f.values,
                    duplicates: duplicateSet.includes(f.field) ? (col.DuplicateValues ?? []) : f.duplicates
                };
            }));
        } catch (e: any) {
            if (axios.isCancel(e) || columnDataCancel.current !== source) return;
            setError({open: true, errorHeader: "Error parsing file", errorMessage: parseFlatFileErrorMessage(e)});
        } finally {
            if (columnDataCancel.current === source) {
                setColumnDataLoading(false);
                columnDataCancel.current = null;
            }
        }
    };
    // Fetch each step's columns as it's reached; re-runs when its inputs arrive/change late.
    useEffect(() => {
        if (!file) return;
        // Wait for parseFile's seed so a new drop doesn't race it.
        if (!parsedFieldsWithData.length) return;
        const sku = profile.AttributeMappings ? profile.AttributeMappings["sku"] : undefined;
        if (activeStep === 2 && profile.Category) {
            fetchColumnData([profile.Category], []);
        } else if (activeStep === 4) {
            // Only list/select-mapped columns (what ListMapping uses) — keeps the URL short (414).
            const listSourceCols = Object.keys(profile.AttributeMappings || {})
                .filter((key) => isListTypeAttribute(attributes.find((a: any) => a.Name === key)))
                .map((key) => (profile.AttributeMappings as any)[key] as string);
            fetchColumnData(listSourceCols, sku ? [sku] : []);
        } else if (activeStep === 5 && sku) {
            // Also fetch here so a stepper-jump past List Mapping still gets dup-SKU detection.
            fetchColumnData([], [sku]);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [activeStep, file, attributes, profile.Category, profile.AttributeMappings, parsedFieldsWithData.length]);
    useEffect(() => {
        return () => {
            if (columnDataCancel.current) { columnDataCancel.current.cancel('unmounted'); columnDataCancel.current = null; }
            if (parseFileCancel.current) { parseFileCancel.current.cancel('unmounted'); parseFileCancel.current = null; }
        };
    }, []);
    // Add effect to cleanup abort controller on step change
    useEffect(() => {
        if (abortController.current) {
            abortController.current.abort();
        }
    }, [activeStep]);
    useEffect(() => {
        if (props.setTitle) {
            props.setTitle('POC: Import', true);
        }
    }, [props]);
    //endregion
    return (
        <>
            <Grid>
                <EnhancedContentLoader updating={updating}/>
                <Grid container style={{gap: pageContentGap, padding: "24px 14px"}}>
                    <Grid item container size={12} justifyContent={"flex-start"} style={{gap: "16px"}}>
                        <IconButton
                            sx={iconButtonSx}
                            style={{height: "36px", width: "36px"}}
                            onClick={(e) => {
                                e.preventDefault();
                                if (props.location.key === undefined)
                                    props.history.push("/productonboardingcenter/catalog?catalog=mycatalog");
                                else
                                    props.history.goBack();
                            }}
                            size="large">
                            <ArrowBack style={{height: "18px", width: "18px"}}/>
                        </IconButton>
                        <Header>
                            {props.isRetailer
                                ? "Import"
                                : isMarketplace && SUPPLIER_SYNDICATION_ENABLED
                                    ? "Source Selection"
                                    : "Import"
                            }
                        </Header>
                    </Grid>
                    <Paper style={{width: "100%", padding: "20px 6px", borderRadius: "5px"}}>
                        <Grid container>
                            <EnhancedStepper
                                steps={steps}
                                activeStep={activeStep}
                                onStepClick={
                                    (hasPreviousImport && file && profile.Partner && profile.Partner !== "000000") ?
                                        (index: number) => {
                                            if(hasPreviousImport && file) {
                                                (SaveProfile as any)(formattedProfile, profileType);
                                                setActiveStep(index);
                                            }
                                        }
                                        : undefined
                                }
                            />
                        </Grid>
                    </Paper>
                    <Grid container style={{gap: pageContentGap, width: "100%"}}>
                        {getStepContent(activeStep,
                            {
                                retailers: props.retailers,
                                isMarketplace: props.retailers && props.retailers.some((retailer: any) => retailer.Marketplace === true),
                                urlPartnerId: urlPartnerId,
                                previousImports: previousImports,
                                isRetailer: props.isRetailer,
                                allowBack, allowNext, backStep, nextStep, activeStep, setActiveStep, steps, setSteps,
                                profile, setProfile, profileType, availableProfiles, blankProfile, loadImportProfiles,
                                formattedProfile, formatProfileFromRemote,
                                parsedFields, parsedFieldsWithData, sampleData,
                                file, onUploadDrop,
                                partners, retailerAttributes: attributes,
                                error, setError,
                                updating, setUpdating, updated, setUpdated,
                                loading, setLoading, columnDataLoading,
                                loadingPreviousImport, loadingPreviousFiles,
                                storageProfileName, storageHelper,
                                history: props.history,
                                location: props.location,
                                abortController: abortController,
                                refreshView: resfreshView
                            }
                        )}
                    </Grid>
                </Grid>
            </Grid>
            {/*region Dialogs*/}
            <EnhancedErrorDialog open={(error as ErrorState).open} errorHeader={(error as ErrorState).errorHeader} errorMessage={(error as ErrorState).errorMessage}
                                 onClose={() => setError({open: false, errorHeader: "", errorMessage: ""})}/>
            <UpdatingDialog
                title={"Loading"}
                subtitle={"Please wait while we load import context."}
                open={Boolean(urlStartingStep && (loadingPreviousImport || loading))}
                onClose={() => setLoading(false)}
            />
            <UpdatingDialog
                title={"Loading"}
                subtitle={"Please wait while we load the previous import."}
                open={loadingPreviousImport && false}
                onClose={() => setLoading(false)}
            />
            {/*endregion*/}
        </>
    );
}
